// Investor "Curated for you" — list + per-deal detail.
// PRD: .local/prds/curated-deals-v1.md (§ 8)
//
// Uses path: /investor/portal/curated  (list) and /investor/portal/curated/:slug (detail)

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Sparkles, Heart, X, Calendar, ChevronLeft, AlertTriangle, Info, Loader2, Check, ArrowRight, Pause } from "lucide-react";

const BASE = import.meta.env.BASE_URL;
const headers = () => ({ "Content-Type": "application/json", "x-investor-token": localStorage.getItem("investor_token") || "" });
const inrShort = (n: number | null | undefined) => {
  if (n == null) return "—"; const x = Number(n);
  if (x >= 10000000) return `₹${(x / 10000000).toFixed(1)}Cr`;
  if (x >= 100000) return `₹${(x / 100000).toFixed(1)}L`;
  return `₹${x.toLocaleString("en-IN")}`;
};

const TYPE_BADGE: Record<string, string> = {
  primary: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  secondary: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

export function InvestorPortalCuratedList() {
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}api/investor/curated-deals`, { headers: headers() });
        if (r.ok) setDeals(await r.json());
      } catch {}
      setLoading(false);
    })();
  }, []);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-[#FF5C00]/30 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-emerald-300" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">Curated for you</h1>
          <p className="text-white/55 text-sm mt-1">Hand-picked opportunities your advisor has sourced. Each one was reviewed by Favcy before it reached you.</p>
        </div>
      </div>

      {loading ? (
        [...Array(2)].map((_, i) => <div key={i} className="h-28 bg-white/[0.04] rounded-xl animate-pulse" />)
      ) : deals.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-10 text-center">
          <Sparkles className="w-10 h-10 text-white/20 mx-auto mb-2" />
          <p className="text-white/55 text-sm">Nothing curated for you right now.</p>
          <p className="text-white/40 text-xs mt-1">When your advisor surfaces something, you'll see it here and get a notification.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {deals.map(d => (
            <Link key={d.id} href={`/investor/portal/curated/${d.slug}`}>
              <a className="block bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.18] rounded-xl p-4 transition-all">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-semibold">{d.title}</h3>
                      <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${TYPE_BADGE[d.dealType]}`}>{d.dealType}</span>
                      {d.status === "paused" && <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-amber-500/30 bg-amber-500/15 text-amber-300">Paused</span>}
                      {d.status === "closed" && <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-white/15 bg-white/[0.06] text-white/55">Closed</span>}
                      {d.myState === "interested" && <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/15 text-emerald-300">You're interested</span>}
                      {d.myState === "committed" && <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-[#FF5C00]/40 bg-[#FF5C00]/15 text-[#FF5C00]">Committed</span>}
                    </div>
                    <p className="text-white/65 text-sm mt-1.5 line-clamp-2">{d.summary}</p>
                    <p className="text-white/45 text-xs mt-2">
                      Curated by {d.advisor?.name || "your advisor"} · {inrShort(d.raiseAmountInr)} · min ticket {inrShort(d.minTicketInr)}
                      {d.closeBy && ` · close by ${new Date(d.closeBy).toLocaleDateString()}`}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/30 flex-shrink-0 mt-1" />
                </div>
              </a>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function InvestorPortalCuratedDetail({ slug }: { slug: string }) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showInterest, setShowInterest] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);
  const [ticket, setTicket] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/investor/curated-deals/${slug}?source=dashboard`, { headers: headers() });
      if (r.ok) setData(await r.json());
      else { const e = await r.json(); setErr(e.error || "Failed to load."); }
    } catch (e: any) { setErr(e?.message || "Network error."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [slug]);

  const expressInterest = async () => {
    setBusy(true);
    const body: any = {};
    if (ticket) body.ticketInrIndicative = Number(ticket);
    if (note) body.note = note;
    const r = await fetch(`${BASE}api/investor/curated-deals/${slug}/interest`, { method: "POST", headers: headers(), body: JSON.stringify(body) });
    setBusy(false);
    if (r.ok) { setShowInterest(false); setTicket(""); setNote(""); load(); }
    else { const e = await r.json(); alert(e.error || "Failed."); }
  };
  const decline = async () => {
    setBusy(true);
    const r = await fetch(`${BASE}api/investor/curated-deals/${slug}/decline`, { method: "POST", headers: headers(), body: JSON.stringify(note ? { reason: note } : {}) });
    setBusy(false);
    if (r.ok) { setShowDecline(false); setNote(""); load(); }
  };
  const requestMeeting = async () => {
    setBusy(true);
    const r = await fetch(`${BASE}api/investor/curated-deals/${slug}/request-meeting`, { method: "POST", headers: headers(), body: JSON.stringify(note ? { note } : {}) });
    setBusy(false);
    if (r.ok) { setShowMeeting(false); setNote(""); load(); }
    else { const e = await r.json(); alert(e.error || "Failed."); }
  };

  if (loading) return <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-10"><Loader2 className="w-6 h-6 animate-spin text-white/55 mx-auto" /></div>;
  if (err) return <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 text-red-300">{err} <Link href="/investor/portal/curated"><a className="underline ml-2">Back</a></Link></div>;
  if (!data) return null;

  const { deal, advisor, distribution } = data;
  const isLive = deal.status === "live";
  const isPaused = deal.status === "paused";
  const isClosed = deal.status === "closed";
  const myState = distribution.state;

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5">
      <Link href="/investor/portal/curated"><a className="text-white/55 hover:text-white text-sm flex items-center gap-1 w-fit"><ChevronLeft className="w-4 h-4" /> All curated deals</a></Link>

      {/* Header */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold text-white">{deal.title}</h1>
          <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${TYPE_BADGE[deal.dealType]}`}>{deal.dealType}</span>
          <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-white/15 text-white/70">{deal.instrumentType.replace(/_/g, " ")}</span>
        </div>
        <p className="text-white/55 text-sm mt-1.5">
          {deal.companyName}{deal.sector ? ` · ${deal.sector}` : ""} · {deal.geography}
          {advisor && <> · Curated by <span className="text-white/85">{advisor.name}</span>{advisor.title && <span className="text-white/55">, {advisor.title}</span>}</>}
        </p>
      </div>

      {isPaused && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200 flex items-start gap-2">
          <Pause className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div><p className="font-semibold">This deal is paused.</p>{deal.pauseReason && <p className="text-amber-200/80 mt-1">{deal.pauseReason}</p>}</div>
        </div>
      )}
      {isClosed && (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-lg p-3 text-sm text-white/70">This deal is closed ({deal.closeReason}).</div>
      )}

      {/* Summary — admin-locked copy */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5">
        <p className="text-sm md:text-base text-white/90 whitespace-pre-wrap leading-relaxed">{deal.summary}</p>
      </div>

      {/* Risk flags */}
      {Array.isArray(deal.riskFlags) && deal.riskFlags.length > 0 && (
        <div className="space-y-1.5">
          {deal.riskFlags.map((f: any, i: number) => (
            <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${
              f.severity === "material" ? "bg-red-500/10 border-red-500/30 text-red-200"
                : f.severity === "caution" ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
                : "bg-blue-500/10 border-blue-500/30 text-blue-200"}`}>
              {f.severity === "material" ? <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />}
              <div><span className="text-[10px] uppercase tracking-wider font-mono mr-2 opacity-80">{f.severity}</span>{f.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Key terms */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Raise / lot" value={inrShort(deal.raiseAmountInr)} />
        <Stat label="Min ticket" value={inrShort(deal.minTicketInr)} />
        <Stat label={deal.dealType === "primary" ? "Pre-money" : "Price/share"} value={inrShort(deal.preMoneyValInr || deal.pricePerShareInr)} />
        <Stat label="Close by" value={deal.closeBy ? new Date(deal.closeBy).toLocaleDateString() : "—"} />
      </div>

      {/* Documents */}
      {(deal.deckUrl || deal.dataRoomUrl) && (
        <div className="flex flex-wrap gap-2">
          {deal.deckUrl && <a href={deal.deckUrl} target="_blank" rel="noopener" className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white">Open deck →</a>}
          {deal.dataRoomUrl && <a href={deal.dataRoomUrl} target="_blank" rel="noopener" className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white">Data room →</a>}
        </div>
      )}

      {/* Action row */}
      {isLive && (
        <div className="border-t border-white/10 pt-4 space-y-3">
          {myState === "interested" && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-200 flex items-center gap-2">
              <Check className="w-4 h-4" /> You marked yourself interested
              {distribution.ticketInrIndicative && <span className="text-emerald-300">(indicative {inrShort(distribution.ticketInrIndicative)})</span>}
              . Your advisor has been notified.
            </div>
          )}
          {myState === "meeting_requested" && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-200 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Meeting requested. Your advisor will reach out to schedule.
            </div>
          )}
          {myState === "committed" && (
            <div className="bg-[#FF5C00]/10 border border-[#FF5C00]/40 rounded-lg p-3 text-sm text-[#FF5C00] flex items-center gap-2">
              <Check className="w-4 h-4" /> Commitment recorded ({inrShort(distribution.ticketInrCommitted)}). Closing happens off-platform.
            </div>
          )}
          {myState === "declined" && (
            <div className="bg-white/[0.04] border border-white/10 rounded-lg p-3 text-sm text-white/55">You declined this deal. You can change your mind by clicking interested below.</div>
          )}

          <div className="flex flex-wrap gap-2">
            {!["committed"].includes(myState) && (
              <button onClick={() => { setShowInterest(true); setShowDecline(false); setShowMeeting(false); }} className="bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-200 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <Heart className="w-4 h-4" /> {myState === "interested" ? "Update interest" : "I'm interested"}
              </button>
            )}
            {!["committed", "meeting_requested"].includes(myState) && (
              <button onClick={() => { setShowMeeting(true); setShowInterest(false); setShowDecline(false); }} className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-200 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Set up a call with {advisor?.name?.split(" ")[0] || "your advisor"}
              </button>
            )}
            {!["committed", "declined"].includes(myState) && (
              <button onClick={() => { setShowDecline(true); setShowInterest(false); setShowMeeting(false); }} className="bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 text-white/70 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                <X className="w-4 h-4" /> Not for me
              </button>
            )}
          </div>

          {showInterest && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 space-y-2">
              <p className="text-sm text-emerald-200">This is a non-binding signal. Your advisor uses it to know who to follow up with first.</p>
              <input type="number" value={ticket} onChange={e => setTicket(e.target.value)} placeholder={`Indicative ticket size (₹) — min ${inrShort(deal.minTicketInr)}`} className="w-full bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-sm text-white" />
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Anything you want your advisor to know? (optional)" rows={2} className="w-full bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-sm text-white resize-y" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowInterest(false)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
                <button onClick={expressInterest} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded flex items-center gap-1">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm</button>
              </div>
            </div>
          )}
          {showDecline && (
            <div className="bg-white/[0.04] border border-white/15 rounded-lg p-4 space-y-2">
              <p className="text-sm text-white/85">No commitment. You can change your mind later.</p>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Reason (optional, helps your advisor curate better next time)" rows={2} className="w-full bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-sm text-white resize-y" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowDecline(false)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
                <button onClick={decline} disabled={busy} className="bg-white/15 hover:bg-white/20 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded">Confirm</button>
              </div>
            </div>
          )}
          {showMeeting && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 space-y-2">
              <p className="text-sm text-blue-200">A task will be created for {advisor?.name || "your advisor"} to schedule a call. They'll send you the meeting link through the regular flow.</p>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="What do you want to discuss? (optional)" rows={2} className="w-full bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-sm text-white resize-y" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowMeeting(false)} className="text-xs text-white/55 hover:text-white px-2">Cancel</button>
                <button onClick={requestMeeting} disabled={busy} className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded flex items-center gap-1">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Request meeting</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Required disclosure */}
      <div className="bg-white/[0.02] border border-white/[0.08] rounded-lg p-3 text-[11px] text-white/45 leading-relaxed">
        <p className="font-mono uppercase tracking-wider text-white/55 mb-1">Disclosure</p>
        Favcy Advisors may receive sourcing and/or success fees from the deal sponsor on commitments made through this opportunity. Favcy retains a platform fee on advisor earnings. This page is not investment advice; do your own diligence and consult a qualified advisor before committing capital.
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{label}</p>
      <p className="text-base font-bold text-white mt-1">{value}</p>
    </div>
  );
}
